/**
 * Schémas Zod — demande d'information à l'assureur (chunk 9.AN.1).
 *
 * Source DB : `app.missing_information` avec `question_audience = 'insurer'`
 * (migration 0076 pour le lien destinataire et l'unicité active).
 *
 * **La demande vise un DESTINATAIRE**, jamais un couple `(dossier, assureur)` :
 * huit couples ont déjà plusieurs destinataires sur des demandes différentes
 * (9.AH.1), et une demande envoyée au mauvais créneau serait invisible.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

/** Le format canonique d'un `field_key`, aligné sur le reste du domaine. */
const FieldPathSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_.]*$/, "field_path must match canonical format")
  .min(2)
  .max(80);

export const CreateInsurerInformationRequestSchema = z.object({
  /** Le créneau assureur précis. Le dossier en est déduit côté serveur. */
  recipient_id: UuidSchema,
  field_path: FieldPathSchema,
  /**
   * Échéance facultative. La colonne existe déjà sur `missing_information` et le
   * worker d'échéances la lit — ce chunk ne crée AUCUN rappel automatique, il se
   * contente de ne pas fermer la porte.
   */
  due_at: z.string().datetime().nullable().optional(),
});
export type CreateInsurerInformationRequestInput = z.infer<
  typeof CreateInsurerInformationRequestSchema
>;

export const InsurerInformationRequestFilterSchema = z.object({
  project_id: UuidSchema,
});
export type InsurerInformationRequestFilter = z.infer<typeof InsurerInformationRequestFilterSchema>;

/** Une demande telle qu'elle revient au wire. Aucun identifiant d'auteur brut. */
export const InsurerInformationRequestRowSchema = z.object({
  id: UuidSchema,
  field_path: z.string(),
  status: z.enum(["open", "in_progress", "fulfilled", "waived", "superseded"]),
  insurer_code: z.string(),
  quote_request_id: UuidSchema,
  scope_recipient_id: UuidSchema,
  due_at: z.string().nullable(),
  created_at: z.string(),
  fulfilled_at: z.string().nullable(),
  /** Non nul quand la demande est close SANS valeur — typiquement « non offert ». */
  waiver_reason: z.string().nullable(),
  requested_by_display_name: z.string().nullable(),
  last_sent_at: z.string().nullable(),
  sent_to: z.string().nullable(),
});
export type InsurerInformationRequestRow = z.infer<typeof InsurerInformationRequestRowSchema>;
